'use client'

import React, { useState, useRef, useEffect } from 'react'
import { useLocalizedNavigation } from '~/hooks/use-localized-navigation'
import ArrowDown from './arrow-down'
import { useRouter } from 'next/navigation'
import { useViewTransitionNavigate } from '~/hooks/use-view-transition-navigate'

interface NavLink {
  href: string
  titleKey: string
  children?: Array<{
    href: string
    titleKey: string
  }>
}

interface DropdownLinkProps {
  link: NavLink
  isScrolled: boolean
  pathname: string
}

export default function DropdownLink({ link, isScrolled, pathname }: DropdownLinkProps) {
  const { getLocalizedHref, getFullUrl } = useLocalizedNavigation()
  const viewNavigate = useViewTransitionNavigate()

  const normalize = (str: string) => str.replace(/\/+$/, '')
  const localizedHref = normalize(getLocalizedHref(link.href))
  const current = normalize(pathname)
  const isActive = current === localizedHref || current.startsWith(localizedHref + '/')
  const router = useRouter()
  const [open, setOpen] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    setOpen(true)
  }
  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setOpen(false), 120)
  }

  useEffect(() => {
    setOpen(false)
  }, [pathname])

  const handleMouseEnterChild = (childHref: string) => {
    router.prefetch(childHref)
  }

  return (
    <div className="relative !ml-0" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <div
        // href={fullUrl}
        // onClick={(e) => {
        //   e.preventDefault()
        //   viewNavigate(localizedHref)
        // }}
        className={`font-body group relative flex cursor-pointer items-center gap-2 pb-1 font-lexend text-base font-light leading-6 transition-colors duration-300 ${
          isActive
            ? isScrolled
              ? 'text-primary-500'
              : 'text-white'
            : isScrolled
              ? 'text-gray-900 hover:text-primary-500'
              : 'text-white hover:text-primary-100'
        }`}
      >
        {link.titleKey}
        <ArrowDown
          className={`ml-0 h-3 w-3 transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
          variant={isActive ? (isScrolled ? 'primary' : 'white') : isScrolled ? 'black' : 'white'}
        />
        {/* Active border */}
        <span
          className={`absolute bottom-0 left-0 h-[2px] transition-all duration-300 ${
            isActive ? (isScrolled ? 'w-full bg-primary-500' : 'w-full bg-white') : 'w-0'
          }`}
        />
        {/* Hover border */}
        <span
          className={`absolute bottom-0 left-0 h-[2px] transition-all duration-300 group-hover:w-full ${
            !isActive ? (isScrolled ? 'w-0 bg-primary-500' : 'w-0 bg-white') : 'w-0'
          }`}
        />
      </div>

      <div
        className={`absolute left-0 top-10 z-50 min-w-[180px] rounded-xl bg-white/95 shadow-xl ring-1 ring-black/10 transition-all duration-300 ${
          open
            ? 'pointer-events-auto translate-y-2 opacity-100'
            : 'pointer-events-none translate-y-0 opacity-0'
        }`}
        style={{ backdropFilter: 'blur(6px)' }}
      >
        <div className="py-2">
          {link.children?.map((child) => {
            const childLocalizedHref = getLocalizedHref(child.href)
            const childFullUrl = getFullUrl(child.href)
            return (
              <a
                key={child.href}
                href={childFullUrl}
                onMouseEnter={() => handleMouseEnterChild(childLocalizedHref)}
                // onClick={(e) => {
                //   e.preventDefault()
                //   viewNavigate(childLocalizedHref)
                // }}
                className="block whitespace-nowrap rounded-lg px-5 py-2 font-lexend text-base font-light leading-6 text-typo-secondary transition-all duration-200 hover:bg-primary-50 hover:text-primary-600"
              >
                {child.titleKey}
              </a>
            )
          })}
        </div>
      </div>
    </div>
  )
}
